import type { Filing } from "./document-filing";
import {
	DOCUMENT_DOMAIN_IDS,
	DOCUMENT_DOMAINS,
	DOCUMENT_TYPE_IDS,
	DOCUMENT_UNIT_IDS,
	DOCUMENT_UNIT_LABELS,
	documentDomainOf,
	documentTypeLabel,
	documentTypesIn,
	documentUnitOf,
	documentUnitsIn,
	isDocumentDomainId,
	isDocumentUnitId,
	type DocumentDomainId,
	type DocumentTypeId,
} from "./document-types";

// Intake's folders: the document type catalogue as a tree a person opens one folder of at a time — its four domains,
// each domain's units and each unit's types — and beside the domains three folders for the documents no type holds. A
// document is in exactly one folder: its type's when Document Filing filed it, or one of the three. The API and the
// fake API list the documents of a folder by these, and the Intake page names and nests them by these.

/**
 * The three folders beside the catalogue's domains: the documents a person is to settle, the ones that are no type of
 * the catalogue, and the ones Document Filing has not filed by it yet.
 */
export const INTAKE_SPECIAL_FOLDERS = ["needs-review", "out-of-scope", "not-filed"] as const;
export type IntakeSpecialFolder = (typeof INTAKE_SPECIAL_FOLDERS)[number];

const INTAKE_SPECIAL_FOLDER_LABELS: Readonly<Record<IntakeSpecialFolder, string>> = {
	"needs-review": "Needs review",
	"out-of-scope": "Out of scope",
	"not-filed": "Not filed yet",
};

/** Every folder of Intake: the domains, the units, the types and the three beside the domains. */
export const INTAKE_FOLDERS = [
	...DOCUMENT_DOMAIN_IDS,
	...DOCUMENT_UNIT_IDS,
	...DOCUMENT_TYPE_IDS,
	...INTAKE_SPECIAL_FOLDERS,
] as const;
export type IntakeFolder = (typeof INTAKE_FOLDERS)[number];

/** The folders a document can be in itself: a type, or one of the three beside the domains. */
export const INTAKE_FILED_IN = [...DOCUMENT_TYPE_IDS, ...INTAKE_SPECIAL_FOLDERS] as const;
export type IntakeFiledIn = (typeof INTAKE_FILED_IN)[number];

/** The longest search the API takes, which is also as much as the Intake page's search box lets anyone type. */
export const INTAKE_QUERY_MAX_LENGTH = 80;

/**
 * The folder a document is in, by what Document Filing read of it: its type's when it filed it; Needs review when a
 * person is to settle what it is; Out of scope when it is no type at all; and Not filed yet when it has not been filed —
 * no filing, or one kept before the catalogue as a contract or as anything else, which waits to be classified.
 */
export function intakeFiledIn(filing: Filing | undefined): IntakeFiledIn {
	if (filing === undefined || (filing.taxonomy === "legacy" && filing.type === undefined)) {
		return "not-filed";
	}
	if (filing.outcome === "needs-review") {
		return "needs-review";
	}
	return filing.outcome === "out-of-scope" || filing.type === undefined ? "out-of-scope" : filing.type;
}

/**
 * Whether a folder holds a document filed in `filedIn`: the folder it is in itself, and the unit and the domain of its
 * type. The top of Intake, no folder at all, holds every document.
 */
export function intakeFolderHolds(folder: IntakeFolder | undefined, filedIn: IntakeFiledIn): boolean {
	return folder === undefined || folder === filedIn || filedIn.startsWith(`${folder}.`);
}

/**
 * The folders inside a folder, in the catalogue's order: at the top, the four domains and then the three folders beside
 * them; inside a domain, its units; inside a unit, its types; and none inside a type or one of the three.
 */
export function intakeFoldersIn(folder: IntakeFolder | undefined): IntakeFolder[] {
	if (folder === undefined) {
		return [...DOCUMENT_DOMAIN_IDS, ...INTAKE_SPECIAL_FOLDERS];
	}
	if (isDocumentDomainId(folder)) {
		return documentUnitsIn(folder);
	}
	return isDocumentUnitId(folder) ? documentTypesIn(folder) : [];
}

function isSpecialFolder(folder: IntakeFolder): folder is IntakeSpecialFolder {
	return (INTAKE_SPECIAL_FOLDERS as readonly string[]).includes(folder);
}

/** What a folder is called: "Finance", "Payables", "Vendor invoice", "Needs review". */
export function intakeFolderLabel(folder: IntakeFolder): string {
	if (isSpecialFolder(folder)) {
		return INTAKE_SPECIAL_FOLDER_LABELS[folder];
	}
	if (isDocumentDomainId(folder)) {
		return DOCUMENT_DOMAINS[folder].label;
	}
	return isDocumentUnitId(folder) ? DOCUMENT_UNIT_LABELS[folder] : documentTypeLabel(folder);
}

/**
 * The way down to a folder from the top, the folder itself last: `fin.ap.vendor_invoice` is in `fin.ap`, which is in
 * `fin`; a domain and one of the three beside the domains are at the top.
 */
export function intakeFolderTrail(folder: IntakeFolder): IntakeFolder[] {
	if (isSpecialFolder(folder) || isDocumentDomainId(folder)) {
		return [folder];
	}
	if (isDocumentUnitId(folder)) {
		return [documentDomainOf(folder), folder];
	}
	return [documentDomainOf(folder), documentUnitOf(folder), folder];
}

/** The domain a folder is, or is in; undefined for one of the three beside the domains. */
export function intakeFolderDomain(folder: IntakeFolder): DocumentDomainId | undefined {
	if (isSpecialFolder(folder)) {
		return undefined;
	}
	return isDocumentDomainId(folder) ? folder : documentDomainOf(folder);
}

/**
 * Whether a document's name, or what Document Filing read of it — who it is with, its number, the name of its type —
 * holds what the search box holds, without regard to case. Every document holds an empty search.
 */
export function intakeDocumentHolds(name: string, filing: Filing | undefined, q: string): boolean {
	const text = q.trim().toLowerCase();
	if (text === "") {
		return true;
	}
	const type: DocumentTypeId | undefined = filing?.type;
	const values = [
		name,
		filing?.counterparty,
		filing?.number,
		type === undefined ? undefined : documentTypeLabel(type),
	];
	return values.some(value => value?.toLowerCase().includes(text) === true);
}
